<script>
  // FILL_THE_LETTERS: the question and text with one box per missing letter.
  // The boxes are injected via {@html} (they sit mid-word in authored
  // markup), so their events are handled by delegation on the root and their
  // values are kept in `answers`, indexed [block][letter].
  import { afterUpdate } from "svelte";
  import { _ } from "svelte-i18n";
  import { joinBlankPartsKeepingWords } from "../bracketBlanks.mjs";
  import { pickBoxLetter, renderLetterBlock } from "../fillTheLetters.mjs";

  export let exercise; // parseFillTheLetters result
  export let answers = [];
  // Set while the "please answer all" warning is up: empty boxes stand out.
  export let showMissing = false;
  export let disabled = false;

  let root;

  $: boxLabel = (block, letter, total) =>
    $_("FILL_THE_LETTERS_BOX_LABEL", { values: { block, letter, total } });
  $: questionHtml = joinBlankPartsKeepingWords(exercise?.fields?.question, (block) =>
    renderLetterBlock(block, boxLabel),
  );
  $: textHtml = joinBlankPartsKeepingWords(exercise?.fields?.text, (block) =>
    renderLetterBlock(block, boxLabel),
  );
  $: hasQuestion = hasVisibleContent(questionHtml);
  $: hasText = hasVisibleContent(textHtml);

  // Same embeds the reports count as content: an image-only question is
  // still a question.
  const EMBED_TAG_PATTERN = /<(img|audio|video|iframe|svg|table)\b/i;

  function hasVisibleContent(html) {
    if (!html) return false;
    if (/class="fl-box"/.test(html) || EMBED_TAG_PATTERN.test(html)) return true;
    return (
      String(html)
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/gi, "")
        .trim().length > 0
    );
  }

  // The markup is rebuilt when the locale changes, which would empty the
  // boxes; `answers` stays the source of truth and is written back here.
  afterUpdate(() => {
    boxes().forEach((box) => {
      const value = answerAt(box);
      if (box.value !== value) box.value = value;
      box.classList.toggle("fl-box-filled", value !== "");
      box.disabled = disabled;
    });
  });

  function boxes() {
    return root ? Array.from(root.querySelectorAll("input.fl-box")) : [];
  }

  function position(box) {
    return {
      block: Number.parseInt(box.dataset.flBlock, 10),
      letter: Number.parseInt(box.dataset.flLetter, 10),
    };
  }

  function answerAt(box) {
    const { block, letter } = position(box);
    return answers?.[block]?.[letter] ?? "";
  }

  function setAnswer(box, value) {
    const { block, letter } = position(box);
    if (!answers[block]) return;
    answers[block][letter] = value;
    answers = answers;
  }

  function focusBox(index) {
    const list = boxes();
    const target = list[index];
    if (!target) return;
    target.focus();
    target.select();
  }

  function boxFrom(event) {
    const target = event?.target;
    return target?.classList?.contains("fl-box") ? target : null;
  }

  function handleInput(event) {
    const box = boxFrom(event);
    if (!box || event.isComposing) return;
    const value = pickBoxLetter(box.value, answerAt(box));
    if (box.value !== value) box.value = value;
    setAnswer(box, value);
    // Typing a letter moves on to the next box, across words.
    if (value) focusBox(boxes().indexOf(box) + 1);
  }

  function handleKeydown(event) {
    const box = boxFrom(event);
    if (!box) return;
    const index = boxes().indexOf(box);

    if (event.key === "Backspace" && box.value === "") {
      // An empty box gives way to the previous one, like a one-time-code field.
      event.preventDefault();
      const previous = boxes()[index - 1];
      if (previous) {
        previous.value = "";
        setAnswer(previous, "");
        focusBox(index - 1);
      }
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      focusBox(index + 1);
    } else if (event.key === " ") {
      event.preventDefault();
    }
  }

  // A box holds one letter: selecting it on focus makes the next keystroke
  // replace that letter instead of being appended to it.
  function handleFocusin(event) {
    boxFrom(event)?.select();
  }
</script>

<div
  class="fl-exercise"
  class:fl-show-missing={showMissing}
  bind:this={root}
  on:input={handleInput}
  on:compositionend={handleInput}
  on:keydown={handleKeydown}
  on:focusin={handleFocusin}
>
  {#if hasQuestion}
    <div class="fl-card fl-question">{@html questionHtml}</div>
  {/if}
  {#if hasText}
    <div class="fl-card fl-text">{@html textHtml}</div>
  {/if}
</div>

<style>
  .fl-exercise {
    margin-top: 0.5rem;
  }

  .fl-card {
    background: #ffffff;
    border: 1px solid #e3edf2;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    padding: 1rem 1.1rem;
    line-height: 2.4;
    overflow-wrap: anywhere;
    -webkit-user-select: none;
    user-select: none;
  }

  .fl-card + .fl-card {
    margin-top: 0.75rem;
  }

  .fl-card :global(p:last-child) {
    margin-bottom: 0;
  }

  /* A block and the letters around it never split across lines. */
  .fl-exercise :global(.blank-word) {
    white-space: nowrap;
  }

  /* The boxes of one block stay on one line. */
  .fl-exercise :global(.fl-block) {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    margin: 0 0.1rem;
    white-space: nowrap;
    vertical-align: middle;
  }

  .fl-exercise :global(.fl-box) {
    width: 1.9rem;
    height: 2.1rem;
    padding: 0;
    font-size: 1.1rem;
    font-weight: 600;
    line-height: 1;
    text-align: center;
    color: #1e293b;
    background: #f8fafc;
    border: 1.5px solid #cbd5e1;
    border-bottom-width: 3px;
    border-radius: 6px;
    -webkit-user-select: text;
    user-select: text;
    caret-color: #00a094;
  }

  .fl-exercise :global(.fl-box:focus) {
    outline: none;
    border-color: #00a094;
    background: #ffffff;
    box-shadow: 0 0 0 2px rgba(0, 160, 148, 0.15);
  }

  .fl-exercise :global(.fl-box.fl-box-filled) {
    background: #ffffff;
    border-color: #94a3b8;
  }

  .fl-exercise :global(.fl-space) {
    display: inline-block;
    width: 0.6rem;
  }

  .fl-show-missing :global(.fl-box:not(.fl-box-filled)) {
    border-color: #f0b429;
    background: #fffbeb;
  }
</style>
